import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/auth";
import { toast } from "sonner";
import { MapPin, Save, Search } from "lucide-react";

/**
 * EA-3 — Operator surface for structured resource geographic coverage.
 *
 * Deliberately narrow scope (per scope doc §5):
 *   - Lists ecosystem entities (PATIENT_VISIBLE only) and their
 *     current coverage state.
 *   - Lets an operator set coverage kind + values per entity.
 *   - Never displays patient locations, patient consent state, or
 *     any patient-facing eligibility signal.
 */
export default function GeographicCoverage() {
  const [entities, setEntities] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [cov, setCov] = useState({ kind: "COUNTRY", values: "", source_version: "1" });
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/ecosystem/entities", { params: { state: "PATIENT_VISIBLE", limit: 100 } });
      setEntities(r.data?.items || []);
      setLoadError(false);
    } catch (e) {
      // React Strict Mode double-invokes effects; the first invocation
      // may race the auth header hydration and produce a spurious
      // 401/403.  Swallow the toast and render an inline retry state.
      // The second invocation typically succeeds; if it does not, the
      // inline notice + Retry button is enough — no red toast.
      setLoadError(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEntity = async (e) => {
    setSelected(e);
    try {
      const r = await api.get(`/ea3/entities/${e.id}/coverage`);
      if (r.data && r.data.kind) {
        setCov({
          kind: r.data.kind,
          values: (r.data.values || []).join(", "),
          source_version: r.data.source_version || String(e.patient_visible_version_id || 1),
        });
      } else {
        setCov({ kind: "COUNTRY", values: "",
                 source_version: String(e.patient_visible_version_id || 1) });
      }
    } catch { /* ignore */ }
  };

  const save = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const values = cov.kind === "REMOTE"
        ? []
        : cov.values.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);
      await api.put(`/ea3/entities/${selected.id}/coverage`, {
        kind: cov.kind, values, source_version: cov.source_version,
      });
      toast.success("Coverage saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save.");
    } finally { setBusy(false); }
  };

  const filtered = entities.filter((e) =>
    !q || (e.canonical_name || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="ea3-coverage-page">
      <div className="flex items-center gap-3">
        <MapPin className="w-6 h-6 text-slate-700" />
        <h1 className="text-2xl font-bold text-slate-900">Geographic coverage</h1>
      </div>
      <p className="text-sm text-slate-600 max-w-2xl">
        Set the structured geographic coverage for each patient-visible
        resource. Coverage answers the question "does this resource apply
        to a patient's area at all?" — never "is this the best resource?".
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-2 mb-3">
            <Search className="w-4 h-4 text-slate-500" />
            <input
              data-testid="ea3-coverage-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search entities…"
              className="flex-1 px-3 py-2 rounded border text-sm"
            />
          </div>
          <ul className="divide-y max-h-[420px] overflow-y-auto">
            {filtered.map((e) => (
              <li key={e.id}>
                <button
                  data-testid={`ea3-coverage-row-${e.id}`}
                  onClick={() => openEntity(e)}
                  className={`w-full text-left px-3 py-2 hover:bg-slate-50 ${selected?.id === e.id ? "bg-slate-50" : ""}`}
                >
                  <div className="font-medium text-slate-900">{e.canonical_name}</div>
                  <div className="text-xs text-slate-500">
                    {e.entity_type} · v{e.patient_visible_version_id}
                  </div>
                </button>
              </li>
            ))}
            {filtered.length === 0 && !loadError && (
              <li className="text-sm text-slate-500 px-3 py-2">No entities found.</li>
            )}
            {filtered.length === 0 && loadError && (
              <li className="text-sm text-slate-600 px-3 py-2 flex items-center justify-between gap-2" data-testid="ea3-coverage-load-error">
                <span>Couldn&rsquo;t load entities.</span>
                <button
                  data-testid="ea3-coverage-retry"
                  onClick={load}
                  className="text-xs font-semibold text-slate-900 underline"
                >Retry</button>
              </li>
            )}
          </ul>
        </div>

        <div className="bg-white rounded-xl border p-5">
          {!selected && (
            <p className="text-sm text-slate-500">Select an entity to edit its coverage.</p>
          )}
          {selected && (
            <div data-testid="ea3-coverage-editor">
              <div className="mb-4">
                <div className="text-xs uppercase text-slate-500 font-semibold">Editing</div>
                <div className="font-semibold text-slate-900">{selected.canonical_name}</div>
              </div>
              <label className="block mb-3">
                <span className="text-xs uppercase text-slate-500 font-semibold">Kind</span>
                <select
                  data-testid="ea3-coverage-kind"
                  value={cov.kind}
                  onChange={(e) => setCov({ ...cov, kind: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded border text-sm"
                >
                  <option value="COUNTRY">COUNTRY — ISO country codes</option>
                  <option value="ADMIN">ADMIN — COUNTRY:STATE pairs</option>
                  <option value="POSTAL">POSTAL — COUNTRY:POSTAL pairs</option>
                  <option value="REMOTE">REMOTE — applies regardless of location</option>
                </select>
              </label>
              {cov.kind !== "REMOTE" && (
                <label className="block mb-3">
                  <span className="text-xs uppercase text-slate-500 font-semibold">
                    Values (comma-separated)
                  </span>
                  <input
                    data-testid="ea3-coverage-values"
                    value={cov.values}
                    onChange={(e) => setCov({ ...cov, values: e.target.value })}
                    placeholder={cov.kind === "COUNTRY" ? "US, GB, IE"
                      : cov.kind === "ADMIN" ? "US:CA, US:NY, GB:ENG"
                      : "US:94103, US:10001"}
                    className="mt-1 w-full px-3 py-2 rounded border text-sm font-mono"
                  />
                </label>
              )}
              <label className="block mb-3">
                <span className="text-xs uppercase text-slate-500 font-semibold">
                  Source version (must equal entity patient-visible version)
                </span>
                <input
                  data-testid="ea3-coverage-source-version"
                  value={cov.source_version}
                  onChange={(e) => setCov({ ...cov, source_version: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded border text-sm"
                />
              </label>
              <button
                data-testid="ea3-coverage-save"
                onClick={save}
                disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2 rounded bg-slate-900 text-white text-sm font-semibold disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> Save coverage
              </button>
              <p className="mt-3 text-xs text-slate-500">
                Saving coverage does not publish the resource, endorse it,
                or make it appear ahead of others. Governance eligibility
                and availability remain the authoritative gates.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
